/**
 * Marketing attribution capture.
 *
 * Captures a visitor's marketing attribution (ad click IDs, UTM params,
 * referrer, landing page) ONCE per browsing session — on first landing — and
 * persists it so it survives navigation between the landing page and the
 * booking-submit moment. If a visitor lands with attribution params and clicks
 * around for ten minutes before booking, the ORIGINAL landing attribution is
 * what we want, not whatever URL they happen to be on when they submit.
 *
 * sessionStorage (not localStorage) is intentional: we want fresh attribution
 * per browsing session, not stale data carried over for weeks.
 */

export const ATTRIBUTION_KEY = 'atomic_attribution_v1';

export interface Attribution {
  fbclid: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
  landingPage: string | null;
  capturedAt: string;
}

/**
 * Capture attribution once per session. Safe to call on every mount/navigation
 * — it no-ops if attribution was already captured this session.
 *
 * Edge cases:
 *  - Missing query params are stored as `null`, never `undefined` (the CRM
 *    rejects `undefined`).
 *  - A returning visitor with no attribution params still gets `referrer` and
 *    `landingPage` captured, so the CRM can classify them as Referral or
 *    Direct/Unknown.
 */
export function captureAttributionOnce(): void {
  if (typeof window === 'undefined') return;
  try {
    if (sessionStorage.getItem(ATTRIBUTION_KEY)) return; // already captured this session

    const params = new URLSearchParams(window.location.search);
    const get = (k: string): string | null => params.get(k) || null;

    const attribution: Attribution = {
      fbclid: get('fbclid'),
      gclid: get('gclid'),
      gbraid: get('gbraid'),
      wbraid: get('wbraid'),
      utm_source: get('utm_source'),
      utm_medium: get('utm_medium'),
      utm_campaign: get('utm_campaign'),
      utm_content: get('utm_content'),
      utm_term: get('utm_term'),
      referrer: document.referrer || null,
      landingPage: window.location.href,
      capturedAt: new Date().toISOString(),
    };

    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // sessionStorage can throw (private mode, quota exceeded). Attribution is
    // best-effort — never let it break the page.
  }
}

/**
 * Read the persisted attribution captured at landing. Returns null if none was
 * stored or the value is unparseable.
 */
export function getStoredAttribution(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}
